import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './LogoMarquee.css';

// Companies I've reported a bug to. `report` is the id of the matching entry in
// the timeline below; clicking a logo jumps to (and opens) that report.
const LOGOS = [
  { name: 'Google', file: 'google.png', report: 'prompt-injection-workspace', shiny: true },
  { name: 'Chess.com', file: 'chess.png', report: 'chesscom-premium' },
  { name: 'Shakepay', file: 'shakepay.png', report: 'shakepay-wcd' },
  { name: 'Super.com', file: 'super.png', report: 'super-deeplink-ato', shiny: true },
  { name: 'Pearson', file: 'pearson.png', report: 'pearson-accesscodes' },
  { name: 'Headspace', file: 'headspace.png', report: 'headspace-content' },
  { name: 'Scaler', file: 'scaler.png', report: 'scaler-recordings' },
  { name: 'SaveMyExams', file: 'savemyexams.png', report: 'savemyexams-xss' },
  { name: 'Mimo', file: 'mimo.png', report: 'mimo-stripe' },
  { name: 'Hack The North', file: 'hackthenorth.png', report: 'htn-ato', shiny: true },
  { name: 'University of Waterloo', file: 'uwaterloo.png', report: 'uwaterloo-tilde' },
  { name: 'Loudmouth Security', file: 'loudmouth.png', report: 'loudmouth-takeover' },
  { name: 'Kenya Revenue Authority', file: 'kra.png', report: 'kra-xss' },
  { name: 'OffSuit', file: 'offsuit.png', report: 'offsuit-logic' },
  { name: 'LaundryCat', file: 'laundrycat.png', report: 'laundrycat-wallet' },
];

export default function LogoMarquee({ onJump }) {
  const trackRef = useRef(null);
  const offset = useRef(0);
  const boost = useRef(0);
  const lastScroll = useRef(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    lastScroll.current = window.scrollY;

    const onScroll = () => {
      const y = window.scrollY;
      // scrolling the page nudges the marquee along, faster the quicker you scroll
      boost.current += (y - lastScroll.current) * 0.22;
      lastScroll.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    let raf;
    const base = 0.16; // gentle constant drift (px/frame)
    const loop = () => {
      const half = track.scrollWidth / 2 || 1; // two copies; wrap at half
      offset.current += (reduce ? 0 : base) + boost.current;
      // ease the scroll kick back down, and keep it from ever lurching
      boost.current *= 0.86;
      boost.current = Math.max(-6, Math.min(6, boost.current));
      if (Math.abs(boost.current) < 0.01) boost.current = 0;
      offset.current = ((offset.current % half) + half) % half;
      track.style.transform = `translate3d(${-offset.current}px, 0, 0)`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const row = (keyPrefix, hidden) =>
    LOGOS.map((l, i) => (
      <li
        className={`marquee__item${l.shiny ? ' marquee__item--shiny' : ''}`}
        key={`${keyPrefix}-${i}`}
        aria-hidden={hidden || undefined}
      >
        <Link
          to={`#${l.report}`}
          onClick={() => onJump && onJump(l.report)}
          title={l.name}
          aria-label={hidden ? undefined : l.name}
          tabIndex={hidden ? -1 : undefined}
          aria-hidden={hidden || undefined}
        >
          <img src={`/company-logos/${l.file}`} alt="" loading="lazy" draggable="false" />
        </Link>
      </li>
    ));

  return (
    <section className="marquee" aria-label="Companies I have reported bugs to">
      <span className="marquee__label">Found bugs for</span>
      <div className="marquee__viewport">
        <ul className="marquee__track" ref={trackRef}>
          {row('a', false)}
          {row('b', true)}
        </ul>
      </div>
    </section>
  );
}
